/**
 * Orders RTK Query slice.
 *
 * Every query endpoint below runs through the three-stage schema pipeline:
 *
 *   rawResponseSchema — validates the envelope { status, code, data, ... }
 *   transformResponse — named unwrap helper from @gearghost/api-schemas/order
 *                       (trivial unwrap, no `|| null` / `?? []` fallbacks)
 *   responseSchema    — validates the unwrapped payload
 *
 * If the backend drops, renames, or retypes a response field without
 * updating the shared schema, RTK Query throws a ZodError at the API
 * boundary instead of silently rendering the stale shape (which used to
 * manifest as $0.00 money rows in the UI).
 *
 */
import { createApi } from "@reduxjs/toolkit/query/react";
import type {
  AcceptOrderRequest,
  AddAuthorizedPersonRequest,
  AddDiscountRequest,
  AddExtraChargeRequest,
  CancelOrderRequest,
  ChangeOrderStatusRequest,
  ChargeOrDiscountIdQuery,
  SubmitAdjustmentBatchRequest,
  CoiUrlResponse,
  OrderStatusType,
  RefundOrderRequest,
  ReplaceVendorNotesRequest,
  ReturnOrderRequest,
  SaveOrderChecklistRequest,
  UploadCoiRequest,
} from "@gearghost/api-schemas/order";
import {
  ORDER_ROUTES,
  SaveOrderChecklistEnvelopeSchema,
  SaveOrderChecklistResultSchema,
  unwrapSaveOrderChecklist,
  OrderDetailEnvelopeSchema,
  OrderDetailSchema,
  OrdersListEnvelopeSchema,
  OrdersListSchema,
  AvailableRefundEnvelopeSchema,
  AvailableRefundSchema,
  CancellationPreviewEnvelopeSchema,
  CancellationPreviewSchema,
  VendorCancellationPreviewEnvelopeSchema,
  VendorCancellationPreviewSchema,
  CalendarOrdersEnvelopeSchema,
  CalendarOrdersSchema,
  VendorNotesEnvelopeSchema,
  VendorNotesSchema,
  VENDOR_NOTES_ROUTES,
  OrderStatusChangeEnvelopeSchema,
  OrderStatusChangeResponseSchema,
  CoiUrlEnvelopeSchema,
  CoiUrlResponseSchema,
  unwrapOrderDetail,
  unwrapOrdersList,
  unwrapAvailableRefund,
  unwrapCancellationPreview,
  unwrapVendorCancellationPreview,
  unwrapCalendarOrders,
  unwrapVendorNotes,
  unwrapOrderStatusChange,
  unwrapCoiUrl,
} from "@gearghost/api-schemas/order";
import { PAYMENT_ROUTES } from "@gearghost/api-schemas/payment";
import {
  ORDER_CHANGE_ROUTES,
  OrderChangeQuoteSchema,
  OrderChangeQuoteEnvelopeSchema,
  unwrapOrderChangeQuote,
  OrderChangeStockSchema,
  OrderChangeStockEnvelopeSchema,
  unwrapOrderChangeStock,
  type OrderChangeStockRequest,
  OrderChangeRequestSchema,
  OrderChangeRequestEnvelopeSchema,
  unwrapOrderChangeRequest,
  ApproveOrderChangeResultSchema,
  type ApproveOrderChangeRequest,
  ApproveOrderChangeEnvelopeSchema,
  unwrapApproveOrderChange,
  type QuoteOrderChangeRequest,
  type RequestOrderChangeRequest,
  type DenyOrderChangeRequest,
} from "@gearghost/api-schemas/orderChange";
import type { z } from "zod";
import axiosBaseQuery from "./axiosBaseQuery";
import { onSchemaFailure } from "./onSchemaFailure";
import { subscribeToSocket } from "@/redux/socketContext";
import { SocketEvent } from "@/services/constants";

/** Which order router a call goes to; the JWT role decides, the URL says it. */
export type OrderPerspective = 'renter' | 'vendor';

/**
 * Arg type + URL builder are shared with mobile (packages/api-schemas
 * order.ts) so both apps serialize the orders list identically. Re-
 * exported here because component callers already import them from this
 * slice.
 */
import {
  buildGetOrdersUrl,
  type GetOrdersArgs,
} from '@gearghost/api-schemas/order';

export { buildGetOrdersUrl };
export type { GetOrdersArgs };

/**
 * Refetch a cache entry whenever the backend notifies this user. The
 * notiCount socket event fires on every notification sent to the signed-in
 * user (a new order, a status change, the other side's change request or
 * its answer), so the entry it names is stale the moment it fires. The
 * orders list and the order detail both hang their entries on it (plan
 * item 96: an order page that is already open learns the other side acted).
 * Bound inside an endpoint's onCacheEntryAdded, so the listener lives
 * exactly as long as the cache entry. subscribeToSocket attaches it
 * whenever a socket is or becomes available, a swap on sign-in included,
 * so a page that loaded before the socket came up still listens; it
 * replaced a wait that checked for the socket every 100ms and gave up
 * after five seconds. No 'connect' listener: the server sends notiCount
 * on every connection too (backend socket/index.js), so a reconnect
 * already reads again what a push missed while the socket was down (a
 * laptop asleep, a dropped network). One on connect as well read the
 * order twice.
 */
const refetchOnNotification = async (
  lifecycle: { cacheDataLoaded: Promise<unknown>; cacheEntryRemoved: Promise<void> },
  invalidate: () => void,
) => {
  await lifecycle.cacheDataLoaded;
  const unsubscribe = subscribeToSocket((socket) => {
    socket.on(SocketEvent.NOTI_COUNT_EVENT, invalidate);
    return () => socket.off(SocketEvent.NOTI_COUNT_EVENT, invalidate);
  });
  await lifecycle.cacheEntryRemoved;
  unsubscribe();
};

/**
 * After a status-change mutation succeeds, patch the order's status in every
 * cached getOrders list so the card updates in-place instead of the whole list
 * refetching and reordering. The new status comes from the backend response
 * (the state machine lives on the backend — no duplicated transition logic here).
 * The list will still refresh on next navigation or via the socket listener.
 *
 * `dispatch` / `getState` / entry iteration use `any` deliberately — the
 * store's RootState type lives elsewhere in the codebase and bringing it
 * in here would drag in transitive typing work that has nothing to do
 * with schema contracts. The iterated RTK Query cache entries' internal
 * shape is also private, so we lean on the runtime guard
 * (`endpointName === "getOrders"`) rather than a compile-time narrow.
 *
 * Declared as a hoisted `function` BELOW the slice (end of file): it reads
 * `ordersApi`, the endpoints call it, and the ordering rule exempts
 * function declarations from use-before-define — the one shape that
 * breaks the cycle without a self-referencing type.
 */

/**
 * What goes stale when money moves on an order from OUTSIDE this slice: the
 * payment slice retries a capture, finishes a bank step, settles a charge,
 * and cannot invalidate these by tag itself. Its callers dispatch
 * `ordersApi.util.invalidateTags(orderPaymentTags(orderId))`. The same two
 * tags this slice's own money mutations invalidate.
 */
export const orderPaymentTags = (orderId: string) => [
  { type: "OrderDetail" as const, id: orderId },
  { type: "Orders" as const, id: "LIST" },
];

export const ordersApi = createApi({
  reducerPath: "ordersApi",
  baseQuery: axiosBaseQuery(),
  onSchemaFailure,
  tagTypes: ["Orders", "OrderDetail", "VendorNotes"],
  endpoints: (builder) => ({
    // Get orders list with search, pagination, sorting, and filters
    getOrders: builder.query<z.infer<typeof OrdersListSchema>, GetOrdersArgs>({
      query: (args = {}) => {
        return buildGetOrdersUrl(args);
      },
      rawResponseSchema: OrdersListEnvelopeSchema,
      transformResponse: unwrapOrdersList,
      responseSchema: OrdersListSchema,
      providesTags: [{ type: "Orders", id: "LIST" }],

      // A notification to this user (new order, status change, a change
      // proposed or answered) means the list is stale: refetch it.
      async onCacheEntryAdded(_arg, { cacheDataLoaded, cacheEntryRemoved, dispatch }) {
        await refetchOnNotification({ cacheDataLoaded, cacheEntryRemoved }, () =>
          dispatch(ordersApi.util.invalidateTags([{ type: "Orders", id: "LIST" }])),
        );
      },
    }),

    // Get single order details
    getOrderDetail: builder.query<
      z.infer<typeof OrderDetailSchema>,
      { orderId: string; type: "vendor" | "renter" }
    >({
      query: ({ orderId, type }) => {
        return type === "vendor"
          ? ORDER_ROUTES.vendorDetail(orderId)
          : ORDER_ROUTES.renterDetail(orderId);
      },
      rawResponseSchema: OrderDetailEnvelopeSchema,
      transformResponse: unwrapOrderDetail,
      responseSchema: OrderDetailSchema,
      providesTags: (_result, _error, { orderId }) => [
        { type: "OrderDetail", id: orderId },
      ],
      // An open order page follows the other side (plan item 96): a change
      // proposed or answered reaches this user as a notification, so the
      // detail refetches on it instead of waiting for a reload.
      async onCacheEntryAdded({ orderId }, { cacheDataLoaded, cacheEntryRemoved, dispatch }) {
        await refetchOnNotification({ cacheDataLoaded, cacheEntryRemoved }, () =>
          dispatch(ordersApi.util.invalidateTags([{ type: "OrderDetail", id: orderId }])),
        );
      },
    }),

    // Accept an order (vendor). Caller passes the orderId string; the
    // wire-shape request body is `{ orderId }` per AcceptOrderRequestSchema.
    acceptOrder: builder.mutation<
      z.infer<typeof OrderStatusChangeResponseSchema>,
      AcceptOrderRequest['orderId']
    >({
      query: (orderId) => ({
        url: ORDER_ROUTES.vendorAccept,
        method: "PUT",
        body: { orderId },
      }),
      rawResponseSchema: OrderStatusChangeEnvelopeSchema,
      transformResponse: unwrapOrderStatusChange,
      responseSchema: OrderStatusChangeResponseSchema,
      invalidatesTags: (_result, _error, orderId) => [
        { type: "OrderDetail", id: orderId },
      ],
      async onQueryStarted(orderId, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          patchOrderStatusInCache(dispatch, getState, orderId, data.status);
        } catch { /* mutation failed — nothing to patch */ }
      },
    }),

    // Cancel or reject an order. The wire-shape request body is
    // `{ orderId, reason? }` per CancelOrderRequestSchema; the `type`
    // arg only chooses which route (renter vs vendor cancel).
    cancelOrder: builder.mutation<
      z.infer<typeof OrderStatusChangeResponseSchema>,
      CancelOrderRequest & { type: 'vendor' | 'renter' }
    >({
      query: ({ orderId, type, reason }) => ({
        url: type === "vendor" ? ORDER_ROUTES.vendorCancel : ORDER_ROUTES.renterCancel,
        method: "PUT",
        body: { orderId, reason },
      }),
      rawResponseSchema: OrderStatusChangeEnvelopeSchema,
      transformResponse: unwrapOrderStatusChange,
      responseSchema: OrderStatusChangeResponseSchema,
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: "OrderDetail", id: orderId },
      ],
      async onQueryStarted({ orderId }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          patchOrderStatusInCache(dispatch, getState, orderId, data.status);
        } catch { /* mutation failed — nothing to patch */ }
      },
    }),

    // Change order status (vendor-triggered: mark as ACTIVE/picked up
    // or CHECKED_IN). Other transitions land via accept, cancel, return,
    // or pg-boss-scheduled jobs.
    changeOrderStatus: builder.mutation<
      z.infer<typeof OrderStatusChangeResponseSchema>,
      ChangeOrderStatusRequest
    >({
      query: ({ orderId, status }) => ({
        url: ORDER_ROUTES.vendorStatus,
        method: "PUT",
        body: { orderId, status },
      }),
      rawResponseSchema: OrderStatusChangeEnvelopeSchema,
      transformResponse: unwrapOrderStatusChange,
      responseSchema: OrderStatusChangeResponseSchema,
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: "OrderDetail", id: orderId },
      ],
      async onQueryStarted({ orderId }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          patchOrderStatusInCache(dispatch, getState, orderId, data.status);
        } catch { /* mutation failed — nothing to patch */ }
      },
    }),

    // Get net refundable amount for an order (vendor refund modal).
    // Returns `{ amount: cents }` — consumers read `result.amount`.
    getAvailableRefund: builder.query<
      z.infer<typeof AvailableRefundSchema>,
      string
    >({
      query: (orderId) => `${PAYMENT_ROUTES.availableRefund}?orderId=${orderId}`,
      rawResponseSchema: AvailableRefundEnvelopeSchema,
      transformResponse: unwrapAvailableRefund,
      responseSchema: AvailableRefundSchema,
    }),

    // Get cancellation refund preview (renter)
    getCancellationPreview: builder.query<
      z.infer<typeof CancellationPreviewSchema>,
      string
    >({
      query: (orderId) => `${ORDER_ROUTES.renterCancelPreview}?orderId=${orderId}`,
      rawResponseSchema: CancellationPreviewEnvelopeSchema,
      transformResponse: unwrapCancellationPreview,
      responseSchema: CancellationPreviewSchema,
    }),

    // Get cancellation refund preview (vendor) — vendor cancel is always
    // a 100% refund of rental + platform fee, so the response shape drops
    // `rentalRefundPercent` and `timing` vs. the renter preview. REQUESTED
    // orders use the Decline flow and don't hit this endpoint.
    getVendorCancellationPreview: builder.query<
      z.infer<typeof VendorCancellationPreviewSchema>,
      string
    >({
      query: (orderId) => `${ORDER_ROUTES.vendorCancelPreview}?orderId=${orderId}`,
      rawResponseSchema: VendorCancellationPreviewEnvelopeSchema,
      transformResponse: unwrapVendorCancellationPreview,
      responseSchema: VendorCancellationPreviewSchema,
    }),

    // Upload COI document — no responseSchema, response is unread by UI
    // (success surfaces only through cache invalidation).
    uploadCoiDocument: builder.mutation<unknown, UploadCoiRequest>({
      query: ({ orderId, file }) => ({
        url: ORDER_ROUTES.renterUploadCoi,
        method: "PATCH",
        body: { orderId, file },
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: "OrderDetail", id: orderId },
      ],
    }),

    // Mint a five-minute URL for downloading the COI document. The returned
    // url is a path-relative `/api/order/{p}/coi/:orderId?token=...` —
    // the consumer (CoiTab) opens it in a new tab and the backend's
    // streaming endpoint authenticates the navigation via the token in
    // the query string (a new-tab GET carries no Authorization header).
    // perspective is part of the arg so vendors and renters call the right
    // route; the backend rejects mismatches.
    getCoiUrl: builder.query<
      CoiUrlResponse,
      { orderId: string; perspective: "renter" | "vendor" }
    >({
      query: ({ orderId, perspective }) =>
        perspective === "vendor"
          ? ORDER_ROUTES.vendorCoiUrl(orderId)
          : ORDER_ROUTES.renterCoiUrl(orderId),
      rawResponseSchema: CoiUrlEnvelopeSchema,
      transformResponse: unwrapCoiUrl,
      responseSchema: CoiUrlResponseSchema,
      // Re-issue when the COI changes; the OrderDetail tag is the source of
      // truth for "this order's COI was uploaded/replaced."
      providesTags: (_result, _error, { orderId }) => [
        { type: "OrderDetail", id: orderId },
      ],
    }),

    // Submit return order (vendor confirms gear is back).
    returnOrder: builder.mutation<
      z.infer<typeof OrderStatusChangeResponseSchema>,
      ReturnOrderRequest
    >({
      query: (body) => ({
        url: ORDER_ROUTES.vendorReturn,
        method: "POST",
        body,
      }),
      rawResponseSchema: OrderStatusChangeEnvelopeSchema,
      transformResponse: unwrapOrderStatusChange,
      responseSchema: OrderStatusChangeResponseSchema,
      invalidatesTags: [{ type: "OrderDetail" }],
      async onQueryStarted(body, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          patchOrderStatusInCache(dispatch, getState, body.orderId, data.status);
        } catch { /* mutation failed — nothing to patch */ }
      },
    }),

    // Add fee to order (vendor) — no responseSchema, response unread.
    addFee: builder.mutation<unknown, AddExtraChargeRequest>({
      query: (body) => ({
        url: ORDER_ROUTES.vendorExtraCharge,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "OrderDetail" }, { type: "Orders", id: "LIST" }],
    }),

    // Add discount to order (vendor) — no responseSchema, response unread.
    addDiscount: builder.mutation<unknown, AddDiscountRequest>({
      query: (body) => ({
        url: ORDER_ROUTES.vendorDiscount,
        method: "PUT",
        body,
      }),
      invalidatesTags: [{ type: "OrderDetail" }, { type: "Orders", id: "LIST" }],
    }),

    // Remove a fee from order (vendor) — no responseSchema, response unread.
    // Caller passes the numeric autoincrement id; backend coerces from
    // query string via ChargeOrDiscountIdQuerySchema.
    removeCharge: builder.mutation<unknown, ChargeOrDiscountIdQuery['id']>({
      query: (id) => ({
        url: `${ORDER_ROUTES.vendorExtraCharge}?id=${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "OrderDetail" }, { type: "Orders", id: "LIST" }],
    }),

    // Remove a discount from order (vendor) — no responseSchema, response unread.
    removeDiscount: builder.mutation<unknown, ChargeOrDiscountIdQuery['id']>({
      query: (id) => ({
        url: `${ORDER_ROUTES.vendorDiscount}?id=${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "OrderDetail" }, { type: "Orders", id: "LIST" }],
    }),

    // Submit the COMPLETED order's draft charges/discounts as one adjustment
    // batch for admin review — no responseSchema, UI reads only success.
    // no responseSchema — the UI reads only success status; the created batch is
    // surfaced on the order-detail refetch (invalidatesTags), not from this body.
    submitAdjustmentBatch: builder.mutation<unknown, SubmitAdjustmentBatchRequest>({
      query: (body) => ({
        url: ORDER_ROUTES.vendorSubmitAdjustmentBatch,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "OrderDetail" }, { type: "Orders", id: "LIST" }],
    }),

    // Add authorized person to order (renter) — no responseSchema, response unread.
    addAuthorizedPerson: builder.mutation<unknown, AddAuthorizedPersonRequest>({
      query: (body) => ({
        url: ORDER_ROUTES.renterAuthorizedPerson,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "OrderDetail" }, { type: "Orders", id: "LIST" }],
    }),

    // The viewer's own ticks on the order's checklist, one list at a time
    // (overhaul plan item 56; decisions 23 and 27). The answer is the ticks
    // as stored, which the card reconciles with; the detail is not refetched
    // for a tick, since the ticks are the only thing that moved.
    saveOrderChecklist: builder.mutation<
      z.infer<typeof SaveOrderChecklistResultSchema>,
      { orderId: string } & SaveOrderChecklistRequest
    >({
      query: ({ orderId, ...body }) => ({
        url: ORDER_ROUTES.checklist(orderId),
        method: "PUT",
        body,
      }),
      rawResponseSchema: SaveOrderChecklistEnvelopeSchema,
      transformResponse: unwrapSaveOrderChecklist,
      responseSchema: SaveOrderChecklistResultSchema,
    }),

    // The store's saved fees and discounts, which the order's pickers offer,
    // are vendorApi's to read: its saves invalidate them, and a second copy
    // here went stale after every save.

    // Refund order (vendor) — no responseSchema, response unread.
    refundOrder: builder.mutation<unknown, RefundOrderRequest>({
      query: (body) => ({
        url: ORDER_ROUTES.vendorRefund,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "OrderDetail" }, { type: "Orders", id: "LIST" }],
    }),

    // Get orders for calendar view (date range, no pagination)
    // ─── Order changes (docs/plans/order-changes.md) ────────────────────
    //
    // `perspective` picks the router (renter or vendor); the body is the
    // shared wire shape. A quote is a read the page re-runs on every edit,
    // so it is a query keyed by the whole proposal; nothing is written
    // until Save sends one request (decision 72).

    quoteOrderChange: builder.query<
      z.infer<typeof OrderChangeQuoteSchema>,
      { perspective: OrderPerspective } & QuoteOrderChangeRequest
    >({
      query: ({ perspective, ...body }) => ({
        url: perspective === 'vendor' ? ORDER_CHANGE_ROUTES.vendorQuote : ORDER_CHANGE_ROUTES.renterQuote,
        method: 'POST',
        body,
      }),
      rawResponseSchema: OrderChangeQuoteEnvelopeSchema,
      transformResponse: unwrapOrderChangeQuote,
      responseSchema: OrderChangeQuoteSchema,
    }),

    // The free stock a Rented Items row's plus stops at while the page
    // edits (decision 125); useChangeStock decides when it runs.
    orderChangeStock: builder.query<
      z.infer<typeof OrderChangeStockSchema>,
      { perspective: OrderPerspective } & OrderChangeStockRequest
    >({
      query: ({ perspective, ...body }) => ({
        url: perspective === 'vendor' ? ORDER_CHANGE_ROUTES.vendorStock : ORDER_CHANGE_ROUTES.renterStock,
        method: 'POST',
        body,
      }),
      rawResponseSchema: OrderChangeStockEnvelopeSchema,
      transformResponse: unwrapOrderChangeStock,
      responseSchema: OrderChangeStockSchema,
    }),

    requestOrderChange: builder.mutation<
      z.infer<typeof OrderChangeRequestSchema>,
      { perspective: OrderPerspective } & RequestOrderChangeRequest
    >({
      query: ({ perspective, ...body }) => ({
        url: perspective === 'vendor' ? ORDER_CHANGE_ROUTES.vendorRequest : ORDER_CHANGE_ROUTES.renterRequest,
        method: 'POST',
        body,
      }),
      rawResponseSchema: OrderChangeRequestEnvelopeSchema,
      transformResponse: unwrapOrderChangeRequest,
      responseSchema: OrderChangeRequestSchema,
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'OrderDetail', id: orderId },
        { type: 'Orders', id: 'LIST' },
      ],
    }),

    // Approve answers with the applied request and, after the card was
    // charged, what Stripe said; the page reads `charge` to show a decline
    // or a bank step.
    approveOrderChange: builder.mutation<
      z.infer<typeof ApproveOrderChangeResultSchema>,
      { perspective: OrderPerspective; orderId: string; requestId: number } & ApproveOrderChangeRequest
    >({
      // The body is the saved card picked on the change checkout for a
      // charge that fires now (decision 21); absent, the card on file.
      query: ({ perspective, requestId, paymentMethodId }) => ({
        url:
          perspective === 'vendor'
            ? ORDER_CHANGE_ROUTES.vendorReview(requestId, 'approve')
            : ORDER_CHANGE_ROUTES.renterReview(requestId, 'approve'),
        method: 'PUT',
        body: paymentMethodId ? { paymentMethodId } : {},
      }),
      rawResponseSchema: ApproveOrderChangeEnvelopeSchema,
      transformResponse: unwrapApproveOrderChange,
      responseSchema: ApproveOrderChangeResultSchema,
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'OrderDetail', id: orderId },
        { type: 'Orders', id: 'LIST' },
      ],
    }),

    // A bank step the approve asked for is finished through the one pair
    // every waiting charge goes through (paymentApi, item 26 of
    // docs/plans/stripe-failure-modes.md): it settles the charge AND applies
    // the change. This slice's own confirmOrderChange was that call's only
    // caller and is gone; the backend route stays.

    denyOrderChange: builder.mutation<
      z.infer<typeof OrderChangeRequestSchema>,
      { perspective: OrderPerspective; orderId: string; requestId: number } & DenyOrderChangeRequest
    >({
      query: ({ perspective, requestId, denialReason }) => ({
        url:
          perspective === 'vendor'
            ? ORDER_CHANGE_ROUTES.vendorReview(requestId, 'deny')
            : ORDER_CHANGE_ROUTES.renterReview(requestId, 'deny'),
        method: 'PUT',
        body: { denialReason },
      }),
      rawResponseSchema: OrderChangeRequestEnvelopeSchema,
      transformResponse: unwrapOrderChangeRequest,
      responseSchema: OrderChangeRequestSchema,
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'OrderDetail', id: orderId },
        { type: 'Orders', id: 'LIST' },
      ],
    }),

    withdrawOrderChange: builder.mutation<
      z.infer<typeof OrderChangeRequestSchema>,
      { perspective: OrderPerspective; orderId: string; requestId: number }
    >({
      query: ({ perspective, requestId }) => ({
        url:
          perspective === 'vendor'
            ? ORDER_CHANGE_ROUTES.vendorReview(requestId, 'withdraw')
            : ORDER_CHANGE_ROUTES.renterReview(requestId, 'withdraw'),
        method: 'PUT',
      }),
      rawResponseSchema: OrderChangeRequestEnvelopeSchema,
      transformResponse: unwrapOrderChangeRequest,
      responseSchema: OrderChangeRequestSchema,
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'OrderDetail', id: orderId },
        { type: 'Orders', id: 'LIST' },
      ],
    }),

    // The vendor's change the renter never answered, sent to GearGhost to
    // answer in the renter's place (Tyler, 2026-10-16). The vendor's alone.
    sendChangeToGearGhost: builder.mutation<
      z.infer<typeof OrderChangeRequestSchema>,
      { orderId: string; requestId: number }
    >({
      query: ({ requestId }) => ({
        url: ORDER_CHANGE_ROUTES.vendorElevate(requestId),
        method: 'PUT',
      }),
      rawResponseSchema: OrderChangeRequestEnvelopeSchema,
      transformResponse: unwrapOrderChangeRequest,
      responseSchema: OrderChangeRequestSchema,
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'OrderDetail', id: orderId },
        { type: 'Orders', id: 'LIST' },
      ],
    }),

    getCalendarOrders: builder.query<
      z.infer<typeof CalendarOrdersSchema>,
      { type?: "vendor" | "renter"; start: string; end: string }
    >({
      query: ({ type = "vendor", start, end }) => {
        const base = type === "vendor" ? ORDER_ROUTES.vendorCalendar : ORDER_ROUTES.renterCalendar;
        return `${base}?start=${start}&end=${end}`;
      },
      rawResponseSchema: CalendarOrdersEnvelopeSchema,
      transformResponse: unwrapCalendarOrders,
      responseSchema: CalendarOrdersSchema,
      providesTags: [{ type: "Orders", id: "CALENDAR" }],
    }),

    // ========== Vendor Notes ==========

    // Get vendor notes for an order (vendor sees all, renter sees visible only)
    getVendorNotes: builder.query<
      z.infer<typeof VendorNotesSchema>,
      { orderId: string; type?: "vendor" | "renter" }
    >({
      query: ({ orderId, type = "vendor" }) =>
        type === "vendor"
          ? VENDOR_NOTES_ROUTES.vendorList(orderId)
          : VENDOR_NOTES_ROUTES.renterList(orderId),
      rawResponseSchema: VendorNotesEnvelopeSchema,
      transformResponse: unwrapVendorNotes,
      responseSchema: VendorNotesSchema,
      providesTags: (_result, _error, { orderId }) => [
        { type: "VendorNotes", id: orderId },
      ],
    }),

    // Save the order's whole list of notes (profile plan decision 41):
    // the vendor's Vendor Notes tab is the Store Documents notes editor,
    // which holds a list and saves it once. The answer is the saved list
    // in order. The one-note add, update, and delete left this slice with
    // the old tab; the endpoints stay for released mobile builds.
    replaceVendorNotes: builder.mutation<
      z.infer<typeof VendorNotesSchema>,
      ReplaceVendorNotesRequest & { orderId: string }
    >({
      query: ({ orderId, ...body }) => ({
        url: VENDOR_NOTES_ROUTES.replace(orderId),
        method: "PUT",
        body,
      }),
      rawResponseSchema: VendorNotesEnvelopeSchema,
      transformResponse: unwrapVendorNotes,
      responseSchema: VendorNotesSchema,
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: "VendorNotes", id: orderId },
      ],
    }),
  }),
});

export const {
  useGetOrdersQuery,
  useLazyGetOrdersQuery,
  useGetOrderDetailQuery,
  useAcceptOrderMutation,
  useCancelOrderMutation,
  useChangeOrderStatusMutation,
  useGetAvailableRefundQuery,
  useLazyGetAvailableRefundQuery,
  useLazyGetCancellationPreviewQuery,
  useLazyGetVendorCancellationPreviewQuery,
  useUploadCoiDocumentMutation,
  useGetCoiUrlQuery,
  useReturnOrderMutation,
  useAddFeeMutation,
  useAddDiscountMutation,
  useRemoveChargeMutation,
  useRemoveDiscountMutation,
  useSubmitAdjustmentBatchMutation,
  useAddAuthorizedPersonMutation,
  useSaveOrderChecklistMutation,
  useRefundOrderMutation,
  useGetCalendarOrdersQuery,
  useLazyGetCalendarOrdersQuery,
  useGetVendorNotesQuery,
  useReplaceVendorNotesMutation,
  useQuoteOrderChangeQuery,
  useOrderChangeStockQuery,
  useRequestOrderChangeMutation,
  useApproveOrderChangeMutation,
  useDenyOrderChangeMutation,
  useWithdrawOrderChangeMutation,
  useSendChangeToGearGhostMutation,
} = ordersApi;

/** The slice of store state this cache patch reads: RTK Query's private
 * per-endpoint entries, narrowed only as far as the runtime guard needs. */
type OrdersCacheEntry = {
  endpointName?: string;
  data?: unknown;
  // Every cache entry carries the args it was fetched with.
  originalArgs: GetOrdersArgs;
};

function patchOrderStatusInCache(
  dispatch: (action: unknown) => void,
  getState: () => unknown,
  orderId: string,
  newStatus: OrderStatusType
) {
  const state = getState() as Record<string, { queries?: Record<string, OrdersCacheEntry> } | undefined>;
  const queries = state[ordersApi.reducerPath]?.queries ?? {};
  for (const entry of Object.values(queries)) {
    if (entry?.endpointName !== "getOrders" || !entry?.data) continue;
    dispatch(
      ordersApi.util.updateQueryData("getOrders", entry.originalArgs, (draft) => {
        const order = draft.orders?.find((o) => o.orderId === orderId);
        if (order) order.status = newStatus;
      })
    );
  }
}
